import { useEffect, useRef, useState } from 'react';

/**
 * The two pieces of state every cutover step's table repeats.
 *
 * Both exist to get around the same thing: these steps write back to Formik through an `onChange`
 * that is a NEW function on every render, and writing to Formik causes a render. An effect that
 * depends on `onChange` therefore re-fires forever. Each hook below holds the callback in a ref and
 * keys its effect on the data instead, which is what stops the loop.
 *
 * Kept as hooks rather than copied into each step because the two defects this pattern already
 * produced - the page-reset loop and the pending-count loop - were each found in one step and had to
 * be reasoned about again in the next. A fourth step should get this right by using it, not by
 * remembering to.
 */

/** Latest value of a callback, readable from an effect that must not depend on its identity. */
const useLatest = <T>(value: T) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

/**
 * Table selection seeded from the rows, once per row set.
 *
 * `seed` decides what is selected when a row set first arrives - every row for the service type
 * step, only the outstanding ones for price plans. It runs again when the row IDs themselves change
 * and not otherwise, so a re-fetch that returns the same rows never overwrites a provider's choice.
 *
 * `hasLoaded` says whether the query behind `rows` has settled, and an empty row set is only
 * meaningful once it has. The two are indistinguishable from `rows` alone - a step that has not
 * fetched yet and a step that found nothing both hold zero rows - and treating "not yet" as "nothing
 * there" would seed an empty selection over a choice the provider had already made. Skipping BOTH is
 * the worse mistake though: a step whose row set genuinely empties out, because the provider went
 * back and changed what is being cut over, would leave the previous set's ids published to the form
 * and post them against the new selection.
 *
 * `publish` is called with the selection every time it changes, seeding included, so the form sees
 * one value however the selection got there.
 */
export function useSeededSelection<T extends { id: string }>(
  rows: T[],
  hasLoaded: boolean,
  seed: (rows: T[]) => string[],
  publish: (ids: string[]) => void,
): [string[], (ids: string[]) => void] {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const syncedRef = useRef<string | null>(null);
  const latest = useLatest({ seed, publish });

  const select = (ids: string[]) => {
    setSelectedIds(ids);
    latest.current.publish(ids);
  };

  useEffect(() => {
    if (!hasLoaded) return;

    const signature = rows.map(({ id }) => id).join(',');
    if (syncedRef.current === signature) return;
    syncedRef.current = signature;

    const seeded = latest.current.seed(rows);
    setSelectedIds(seeded);
    latest.current.publish(seeded);
    // Keyed on `rows` and `hasLoaded` alone. `seed` and `publish` are read through the ref on purpose
    // - depending on either would re-run this on every render, which is the loop described at the top
    // of the file.
  }, [rows, hasLoaded]);

  return [selectedIds, select];
}

/** Writes a derived value out to the form, but only when the value itself has changed. */
export function usePublishedValue<T>(value: T, publish: (value: T) => void): void {
  const publishedRef = useRef<T | undefined>(undefined);
  const latest = useLatest(publish);

  useEffect(() => {
    if (publishedRef.current === value) return;
    publishedRef.current = value;
    latest.current(value);
    // Keyed on `value` alone, for the same reason: `publish` changes identity on every render and
    // depending on it loops.
  }, [value]);
}
